import axios from "axios";

import { env } from "@/config/env";
import { registerInterceptors } from "@/lib/interceptors";

/**
 * The single Axios instance every feature's API calls go through — nobody
 * else calls `axios` directly, and nothing outside `src/services/base-api.ts`
 * should import this either (feature code calls the typed helpers there,
 * not raw HTTP verbs). Request/response behavior (token attachment, error
 * normalization, 401 handling) lives in src/lib/interceptors.ts, not here —
 * this file only decides what the client *is* (base URL, timeout, default
 * headers).
 *
 * `withCredentials: true` because the refresh token travels as an httpOnly
 * cookie, never in a JSON body (docs/architecture/security-architecture.md's
 * "cookies must be Secure in production" only makes sense if something is
 * actually stored in one) — the browser only attaches that cookie to
 * cross-origin requests if this is set, and the server's CORS config must
 * echo it with `credentials: true` and a specific (non-`*`) origin.
 *
 * `timeout: 60000`, not the original 15000 — the production server (Render
 * free tier) spins down after inactivity, and its own dashboard warns the
 * first request after that "can delay requests by 50 seconds or more."
 * Caught live: a 15s timeout meant a cold-start registration request threw
 * "timed out" on the client while the server kept processing and completed
 * successfully anyway (the user row landed in MongoDB, OTP hash and all) —
 * the client just stopped waiting before the response could arrive. This
 * doesn't eliminate the cold-start delay itself (that needs a paid Render
 * plan to avoid), it just stops the client from giving up on a request the
 * server is still legitimately working through.
 */
export const httpClient = axios.create({
  baseURL: env.VITE_API_URL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

registerInterceptors(httpClient);
